import React, { useEffect, lazy } from "react";
import PageView from "./PageView";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import type Page from "../models/Page";
import { BottomTabsWrapper } from "./BottomTabs";
import type Device from "../models/Device";
import DeviceTemplatePropertiesView from "./DeviceTemplatePropertiesView";
import type Scene from "../models/Scene";

import Logger from "../Logger";
import type DeviceTemplate from "../models/DeviceTemplate";
import PdfPageReference from "../models/PdfPageReference";

import HomePane from "./HomePane";

import PaneObject from "../models/PaneObject";
import HelpPane from "./HelpPane";
import { createPortal } from "react-dom";
import Browser from "../browser";
import type Fuse from "../models/Fuse";
import type TerminalStrip from "../models/TerminalStrip";
import type Room from "../models/Room";
import type Actor from "../models/Actor";
import EstadaSuspense from "./EstadaSuspense";
import { itx, noItx } from "../translationItx/itx";

import TemplateGroup from "../models/TemplateGroup";
import ActivePanes from "../models/ActivePanes";
import { type CableType } from "../models/CableType";
import type Consumer from "../models/Consumer";
import TabItemData from "../models/TabItemData";
import type Line from "../models/Line";
import type BaseModelMap from "../models/BaseModelMap";
import type Area from "../models/Area";
import PageGroup from "../models/PageGroup";
import { PageTypes } from "../models/PageTypes";
import { loadReactPdf } from "../getPdfRenderer";
import Cabinet from "../models/Cabinet";
import { isUiModeProductAdmin } from "../uiMode";
import Floor from "../models/Floor";

const logger = new Logger();

function LazyC1Viewer(props: { item: Floor | Room; activePanes: ActivePanes }) {
  return <EstadaSuspense Component={lazy(() => import("../../ConceptOne/src/viewer/C1_Viewer"))} props={props} />;
}

const ProductSelectionPane = lazy(() => import("./ProductSelectionPane"));

////////////////////////////////////////////////////////////////////////
//
// All Views should be bould lazy !!!!!!!!!!!

function LazyRealeaseNotesPane() {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./RealeaseNotesPane");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => <div>{itx("WorkingPanes.releaseNote", "Release Notes konnten nicht geladen werden")}</div>,
          });
        }
      })}
    />
  );
}

function LazyActorInstallationView(props: { actor: Actor }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./ActorInstallationView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>{itx("WorkingPanes.actorInstallation", "Actor Installation konnte nicht geladen werden")}</div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyRoomInstallationView(props: { room: Room }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./RoomInstallationView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>{itx("WorkingPanes.roomInstallation", "Raum Installation konnte nicht geladen werden")}</div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyEtsPostImportPane(props: {}) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("../extensions/imports/ets/postImport/pane_PostImport");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>{itx("WorkingPanes.roomInstallation", "Raum Installation konnte nicht geladen werden")}</div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyAiPane(props: {}) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./AiPane");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => <div>{itx("WorkingPanes.aiPane", "AI Pane konnte nicht geladen werden")}</div>,
          });
        }
      })}
      props={props}
    />
  );
}

function LazyCommander(props: {}) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./Commander");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => <div>{itx("WorkingPanes.commander", "Commander konnte nicht geladen werden")}</div>,
          });
        }
      })}
      props={props}
    />
  );
}
function LazyAreaPropertiesView(props: { area: Area }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./AreaPropertiesView/AreaPropertiesView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>{itx("WorkingPanes.areaProperties", "Bereichseigenschaften konnten nicht geladen werden")}</div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyFusePropertiesView(props: { fuse: Fuse }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./FusePropertiesView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>{itx("WorkingPanes.fuseProperties", "Sicherungseigenschaften konnten nicht geladen werden")}</div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}
function LazyCableTypePropertiesView(props: { cableType: CableType }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./CableTypePropertiesView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>
                {itx("WorkingPanes.cableTypeProperties", "Kabeltyp-Eigenschaften konnten nicht geladen werden")}
              </div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}
function LazyTemplateGroupPropertiesView(props: { templateGroup: TemplateGroup }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./TemplateGroupPropertiesView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>
                {itx(
                  "WorkingPanes.templateGroupProperties",
                  "Vorlagengruppen-Eigenschaften konnten nicht geladen werden",
                )}
              </div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyTerminalStripPropertiesView(props: { terminalStrip: TerminalStrip }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./TerminalStripPropertiesView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>
                {itx("WorkingPanes.terminalStripProperties", "Klemmleisten-Eigenschaften konnten nicht geladen werden")}
              </div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}
function LazyConsumerPropertiesView(props: { consumer: Consumer }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./ConsumerPropertiesView/ConsumerPropertiesView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>
                {itx("WorkingPanes.consumerProperties", "Verbraucher-Eigenschaften konnten nicht geladen werden")}
              </div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyPdfPageView(props: { pdfPageReference: PdfPageReference }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./PdfPageView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => <div>{itx("WorkingPanes.pdfPageView", "PDF Seite konnte nicht geladen werden")}</div>,
          });
        }
      })}
      props={props}
    />
  );
}

function LazyScenePropertiesView(props: { obj: Scene }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./SceneProperties/SceneProperties");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>{itx("WorkingPanes.sceneProperties", "Szenen-Eigenschaften konnten nicht geladen werden")}</div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyMultiplexerPropertiesView(props: { obj: Scene }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./MulipexerProperties/MultiplexerProperties");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>
                {itx("WorkingPanes.multiplexerProperties", "Multiplexer-Eigenschaften konnten nicht geladen werden")}
              </div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyScriptEditor(props: {
  device: Device;
  instanceId?: string;
  propertyId?: string;
  label?: string;
  standAlone?: boolean;
}) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./KnxScriptEditor");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => <div>{itx("WorkingPanes.scriptEditor", "Script Editor konnte nicht geladen werden")}</div>,
          });
        }
      })}
      props={props}
    />
  );
}

function LazyTopologyLineView(props: { lineOrAreas: Line | BaseModelMap<Area> }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("./TopologyLineView");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => (
              <div>{itx("WorkingPanes.topologyLineView", "Topologie Ansicht konnte nicht geladen werden")}</div>
            ),
          });
        }
      })}
      props={props}
    />
  );
}

function LazyCabinetLayoutView(props: { cabinet: Cabinet; activePanes: ActivePanes }) {
  return (
    <EstadaSuspense
      Component={lazy(() => {
        try {
          return import("../CabinetLayout/CabinetLayoutPane");
        } catch (e) {
          console.error(e);
          return Promise.resolve({
            default: () => <div>{itx("WorkingPanes.cabinetLayout", "Cabinet Layout konnte nicht geladen werden")}</div>,
          });
        }
      })}
      props={props}
    />
  );
}

const _LazyPdfListViewerComponent = lazy(async () => {
  await loadReactPdf();
  return import("./PdfLists/Components/PdfListViewer");
});

function LazyPdfListViewer(props: {
  page: Page;
  activePanes: ActivePanes;
  initialPage?: number;
  onPageChange?: (pageNumber: number) => void;
}) {
  return <EstadaSuspense Component={_LazyPdfListViewerComponent} props={props} />;
}

///////////////////////////////////////////////////////////////////////////////////

export default function WorkingPanes(props: {
  isScreen2: boolean;
  activePanes: ActivePanes;
  tabItems: Array<TabItemData>;
}) {
  const { activePanes, tabItems, isScreen2 } = props;

  useEffect(() => {
    state.activePanes.updateTabs();
    function handleMousedown(e: MouseEvent) {
      if (state.activePanes2) {
        let target = e.target as HTMLElement;
        while (target && !target.id.startsWith("pane_")) {
          target = target.parentElement as HTMLElement;
        }
        if (target) {
          const id = target.id.substring(5 /* "pane_".length */);
          if (id) {
            state.activeDocumentScreen = (window as any).isScreen2 ? 2 : 1;
            state.activePanes.updateTabs();
            state.activePanes2.updateTabs();
            state.command("updateMenubar"); // no itx
          }
        }
      }
    } // handleMousedown

    const elm = document.getElementById("WorkingPanes");
    if (elm) {
      elm.addEventListener("mousedown", handleMousedown);
    }

    return () => {
      if (elm) {
        elm.removeEventListener("mousedown", handleMousedown);
      }
    };
  }, []);

  useOnChangeWatcher(activePanes.activeItemIndex);

  logger.log("render WorkingPanes #of panes: ", activePanes.items.length);

  const pageViewElements: any = [];
  const activePaneIndex = activePanes.activeItemIndex.value;

  // const tabItems: any = []; //  useRef(state.tabItems);

  tabItems.splice(0, tabItems.length);

  activePanes.items.forEach((pane, paneIndex) => {
    if (!isUiModeProductAdmin() && (pane.item?.className === noItx("Line") || pane.item === state.topologies)) {
      return;
    }

    // activeItemIndex >= 0) && state.pages.activeItems[state.pages.activeItemIndex]
    logger.log("render WorkingPanes push page ", pane);
    const tabItemData = new TabItemData(pane.id, pane.tag, pane, pane.closeable, pane.screen2);
    tabItemData.subPageId = pane.subPageId;
    tabItemData.title = pane.title || "";

    // For all PDF-rendered documents (lists, floor/room plan PDFs, uploaded building plans)
    // show only the name in the tab — no tag. Interactive drawing pages (roomplan/floorplan)
    // keep the tag. BottomTabs skips the tag block when title is non-empty.
    if (pane.item?.className === noItx("Page") && !tabItemData.title) {
      const pageType = (pane.item as Page).documentType?.type;
      if (pageType !== PageTypes.roomplan && pageType !== PageTypes.floorplan) {
        tabItemData.title = pane.name;
      }
    }

    pane.tabItemData = tabItemData;

    tabItems.push(tabItemData);

    if (paneIndex !== activePaneIndex) {
      return;
    }

    let elm: any;

    if (pane.item) {
      const className = pane.item.className;

      switch (className) {
        case noItx("PaneObject"):
          {
            switch ((pane.item as PaneObject).type) {
              case noItx("home"):
                elm = <HomePane key="home" />;
                break;
              case noItx("commander"):
                elm = <LazyCommander key="commander" />;
                break;
              case noItx("help"):
                elm = <HelpPane key="help" />;
                break;
              case noItx("ai"):
                elm = <LazyAiPane key="ai" />;
                break;
              case noItx("etsPostImport"):
                elm = <LazyEtsPostImportPane key="etsPostImport" />;
                break;

              case noItx("releaseNotes"):
                elm = <LazyRealeaseNotesPane key="realeasenotes" />;
            }
          }
          break;

        case noItx("PdfPageReference"):
          elm = <LazyPdfPageView key={pane.item.id} pdfPageReference={pane.item as PdfPageReference} />;
          break;

        case noItx("Floor"):
          elm = <LazyC1Viewer key={pane.item.id} item={pane.item as Floor} activePanes={activePanes} />;
          break;

        case noItx("Room"):
          elm = <LazyC1Viewer key={pane.item.id} item={pane.item as Room} activePanes={activePanes} />;
          break;

        case noItx("Page"): {
          const page = pane.item as Page;
          if (page.documentType?.type == PageTypes.roomplan || page.documentType?.type == PageTypes.floorplan) {
            elm = <PageView key={pane.item.id} page={pane.item as Page} activePanes={activePanes} />;

            // } else if (pane.item?.className == "Actor") {
            //   elm = <ActorPropertiesView actor={pane.item as Actor} />;
          } else {
            elm = (
              <LazyPdfListViewer
                key={`${pane.item.id}_${pane.subPageId ?? ""}`}
                page={pane.item as Page}
                activePanes={activePanes}
                initialPage={pane.lastViewedPage ?? (pane.subPageId ? Number(pane.subPageId) : undefined)}
                onPageChange={(pageNumber) => {
                  pane.lastViewedPage = pageNumber;
                }}
              />
            );
          }

          break;
        }

        case noItx("PageGroup"): {
          elm = (
            <LazyPdfListViewer
              key={pane.item.id}
              page={(pane.item as PageGroup).pages.asArray()[0] as Page}
              activePanes={activePanes}
              initialPage={pane.lastViewedPage}
              onPageChange={(pageNumber) => {
                pane.lastViewedPage = pageNumber;
              }}
            />
          );
          break;
        }
        case noItx("Room"):
          elm = (pane.item as Room).c1 ? (
            <LazyC1Viewer key={pane.item.id} item={pane.item as Room} activePanes={activePanes} />
          ) : (
            <LazyRoomInstallationView key={pane.item.id} room={pane.item as Room} />
          );
          break;

        case noItx("Actor"):
        case noItx("Button"):
          if (state.settings.diagnosticMode) {
            elm = <LazyActorInstallationView key={pane.item.id} actor={pane.item as Actor} />;
          } else {
            if (pane.subPageId == "scriptEditor") {
              elm = <LazyScriptEditor key={pane.item.id} device={pane.item as Device} standAlone={true} />;
            } else {
              elm = <DeviceTemplatePropertiesView key={pane.item.id} deviceOrDeviceTemplate={pane.item as Device} />;
            }
          }
          break;

        case noItx("DeviceTemplate"):
          // elm = <TemplatePropertiesView key={pane.item.id} template={pane.item as DeviceTemplate} />;
          elm = (
            <DeviceTemplatePropertiesView key={pane.item.id} deviceOrDeviceTemplate={pane.item as DeviceTemplate} />
          );
          break;

        case noItx("Scene"):
          elm = <LazyScenePropertiesView key={pane.item.id} obj={pane.item as Scene} />;
          break;

        case noItx("Multiplexer"):
          elm = <LazyMultiplexerPropertiesView key={pane.item.id} obj={pane.item as Scene} />;
          break;

        case noItx("Fuse"):
          elm = <LazyFusePropertiesView key={pane.item.id} fuse={pane.item as Fuse} />;
          break;

        case noItx("Area"):
          elm = <LazyAreaPropertiesView key={pane.item.id} area={pane.item as Area} />;
          break;
        case noItx("CableType"):
          elm = <LazyCableTypePropertiesView key={pane.item.id} cableType={pane.item as CableType} />;
          break;

        case noItx("TemplateGroup"):
          elm = <LazyTemplateGroupPropertiesView key={pane.item.id} templateGroup={pane.item as TemplateGroup} />;
          break;

        case noItx("TerminalStrip"):
          elm = <LazyTerminalStripPropertiesView key={pane.item.id} terminalStrip={pane.item as TerminalStrip} />;
          break;
        case noItx("Consumer"):
          elm = <LazyConsumerPropertiesView key={pane.item.id} consumer={pane.item as Consumer} />;
          break;

        case noItx("Cabinet"):
          elm = <LazyCabinetLayoutView key={pane.item.id} cabinet={pane.item as Cabinet} activePanes={activePanes} />;
          break;

        case noItx("BaseModelMap"):
          if (pane.item == state.topologies) {
            elm = <LazyTopologyLineView key={pane.item.id} lineOrAreas={pane.item as BaseModelMap<Area>} />;
          } else if (pane.item === state.templateGroups) {
            elm = <EstadaSuspense Component={ProductSelectionPane} props={{ groups: state.templateGroups }} />;
          }
          break;

        case noItx("Line"):
          elm = <LazyTopologyLineView key={pane.item.id} lineOrAreas={pane.item as Line} />;
          break;

        default:
          elm = (
            <div key={pane.item?.id}>
              {noItx("item not supported: className==")}
              {pane.item?.className}
            </div>
          );
          break;
      }

      pageViewElements.push(<React.Fragment key={`${pane.id}_${pane.subPageId ?? ""}`}>{elm}</React.Fragment>);
    }
  });

  const bottomTabHost = document.getElementById("bottomTabHost");
  if (bottomTabHost) {
    createPortal(
      <BottomTabsWrapper
        items={tabItems}
        active={activePanes.activeItemIndex.value}
        onClose={(index, id) => {
          activePanes.closePane(id);
        }}
        onActivate={(index, id) => {
          activePanes.setActiveId(id);
        }}
        watchIndex={activePanes.activeItemIndex}
        activePanes={activePanes}
        moveToScreen={isScreen2 ? 1 : 2}
      />,
      bottomTabHost,
    );
  }

  return (
    <div
      id="WorkingPanes"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "calc(100% - 35px)",
          flexShrink: "1",
          flexGrow: "1",
          // height: `calc(100% - ${Layout.PageView.bottomTabHeight})`,
          // overflow: "hidden",
        }}
      >
        {pageViewElements}
      </div>

      {Browser.isLandScape() && (
        <div
          id="bottomTabHost"
          className="documentTabs"
          style={{
            position: "relative",
            display: "flex",
            width: "100%",
            flexGrow: "0",
            flexShrink: "0",
            height: "35px",
            marginTop: "-10px",
            pointerEvents: "none",
            paddingLeft: "10px",
          }}
        >
          <BottomTabsWrapper
            items={tabItems}
            active={activePanes.activeItemIndex.value}
            onClose={(index, id, subPageId) => {
              activePanes.closePane(id, subPageId);
            }}
            onActivate={(index, id, subPageId) => {
              activePanes.setActiveId(id, subPageId);
            }}
            watchIndex={activePanes.activeItemIndex}
            moveToScreen={isScreen2 ? 1 : 2}
            activePanes={activePanes}
          />
        </div>
      )}
    </div>
  );
}
